/**
 * Dashboard Main Initialization
 * Entry point for dashboard functionality
 */
const Dashboard = {
  config: null,
  modules: {},

  /**
   * Initialize dashboard
   * @param {object} serverConfig - Configuration from server
   */
  async init(serverConfig) {
    this.config = serverConfig;

    try {
      // 1. Initialize modules (map shell only — markers deferred)
      await this.initializeModules();

      // 2. Load active tab + summary first (fast path)
      await this.loadInitialData();

      // 3. Build marquee from already-loaded station data (no extra API)
      this.refreshMarqueeFromStations();

      // 4. Defer map markers after active tab is ready
      this.deferMapMarkers();

      // 5. Setup event listeners
      this.setupEventListeners();

      // 6. Setup auto-refresh intervals
      this.setupIntervals();

      // 7. Initialize UI components
      this.initializeUIComponents();
    } catch (error) {
      console.error("❌ Dashboard initialization failed:", error);
      this.handleInitError(error);
    }
  },

  /**
   * Initialize all modules
   */
  async initializeModules() {
    console.log("📦 Initializing modules...");

    if (typeof StationModule !== "undefined") {
      this.modules.station = StationModule;
      StationModule.init();
    }

    if (typeof ChartModule !== "undefined") {
      this.modules.chart = ChartModule;
      ChartModule.init();
    }

    if (typeof CCTVModule !== "undefined") {
      this.modules.cctv = CCTVModule;
      CCTVModule.init();
    }

    if (typeof AnimationModule !== "undefined") {
      this.modules.animation = AnimationModule;
      AnimationModule.init();
    }

    if (typeof OwAnimationModule !== "undefined") {
      this.modules.owAnimation = OwAnimationModule;
      OwAnimationModule.init();
    }

    // Map init is isolated — failure must not block stations, charts, or CCTV
    if (typeof MapModule !== "undefined") {
      this.modules.map = MapModule;
      try {
        await MapModule.init({
          latitude: this.config.mapLatitude,
          longitude: this.config.mapLongitude,
          zoom: this.config.mapZoom,
          deferMarkers: true,
        });
        requestAnimationFrame(() => {
          MapModule.triggerResize();
          setTimeout(() => MapModule.triggerResize(), 150);
        });
      } catch (err) {
        console.error("Map module failed to initialize:", err);
      }
    }
  },

  getActiveTabId() {
    return (
      localStorage.getItem(DashboardConstants.STORAGE_KEYS.ACTIVE_TAB) ||
      "tab-awlr"
    );
  },

  /**
   * Load summary + semua tipe yang dipakai marquee (supaya marquee tidak kosong
   * sebelum tab diklik). Tab lain di luar marquee tetap lazy.
   */
  async loadInitialData() {
    const activeTab = this.getActiveTabId();
    StationModule.resetLoadedTabs();

    const totals =
      (typeof DashboardServerConfig !== "undefined" &&
        DashboardServerConfig.totals) ||
      {};

    const tabsToLoad = new Set();
    if (totals.awlr > 0) tabsToLoad.add("tab-awlr");
    if (totals.arr > 0) tabsToLoad.add("tab-arr");
    if (totals.awlrArr > 0) tabsToLoad.add("tab-awlr-arr");
    if (totals.aws > 0) tabsToLoad.add("tab-aws");
    if (totals.flowMeter > 0) tabsToLoad.add("tab-fm");
    if (activeTab) tabsToLoad.add(activeTab);

    await Promise.all([
      this.loadSummary(),
      ...[...tabsToLoad].map((tabId) =>
        StationModule.ensureTabLoaded(tabId, { force: true }),
      ),
    ]);

    this.refreshMarqueeFromStations();
  },

  deferMapMarkers() {
    if (
      typeof MapModule === "undefined" ||
      !MapModule.fetchMarkers ||
      !MapModule.isReady()
    ) {
      return;
    }

    // Yield to paint active tab first, then fetch slim markers.
    setTimeout(() => {
      MapModule.fetchMarkers().catch((error) => {
        console.error("Error loading deferred markers:", error);
      });
      if (typeof MapModule.loadRiverLayers === "function") {
        MapModule.loadRiverLayers().catch(() => {});
      }
      if (typeof MapModule.loadWatershedLayers === "function") {
        MapModule.loadWatershedLayers().catch(() => {});
      }
    }, 0);
  },

  /**
   * Build marquee from StationModule state — avoids /Home/Marquee re-query.
   */
  refreshMarqueeFromStations() {
    try {
      const state = StationModule.getState();
      const stations = state.stations || {};
      let textMarquee = "";

      if (stations.awlr && stations.awlr.length > 0) {
        textMarquee += this.generateMarqueeAwlr(stations.awlr);
      }
      if (stations.arr && stations.arr.length > 0) {
        if (textMarquee) textMarquee += this._marqueeSep();
        textMarquee += this.generateMarqueeArr(stations.arr);
      }
      if (stations.awlrArr && stations.awlrArr.length > 0) {
        if (textMarquee) textMarquee += this._marqueeSep();
        textMarquee += this.generateMarqueeAwlrArr(stations.awlrArr);
      }
      if (stations.aws && stations.aws.length > 0) {
        if (textMarquee) textMarquee += this._marqueeSep();
        textMarquee += this.generateMarqueeAws(stations.aws);
      }
      if (stations.flowMeter && stations.flowMeter.length > 0) {
        if (textMarquee) textMarquee += this._marqueeSep();
        textMarquee += this.generateMarqueeFlowMeter(stations.flowMeter);
      }

      if (textMarquee) {
        $("#marquee").html(textMarquee);
      }
    } catch (error) {
      console.error("Error refreshing marquee:", error);
    }
  },

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Window resize
    window.addEventListener(
      "resize",
      DashboardHelpers.debounce(() => {
        DashboardHelpers.adjustPadding();
        if (this.modules.map) MapModule.triggerResize();
        if (this.modules.chart) ChartModule.handleResize();
        if (this.modules.animation) AnimationModule.handleResize();
        if (this.modules.owAnimation) OwAnimationModule.handleResize();
      }, 250),
    );

    // Set active tab on page load
    const activeTab =
      localStorage.getItem(DashboardConstants.STORAGE_KEYS.ACTIVE_TAB) ||
      "tab-awlr";
    $(`#tab-center-left a[href="#${activeTab}"]`).addClass("active");
    $(`#${activeTab}`).addClass("show active");

    // Tab changes (lazy-load handled in StationModule; keep storage in sync)
    $("#tab-center-left a").on("click", (e) => {
      const selectedTab = $(e.currentTarget).attr("href").replace("#", "");
      localStorage.setItem(
        DashboardConstants.STORAGE_KEYS.ACTIVE_TAB,
        selectedTab,
      );
      StationModule.ensureTabLoaded(selectedTab);
      setTimeout(DashboardHelpers.adjustPadding, 100);
    });

    // Search functionality
    const searchInput = document.getElementById("filter-pos");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.filterStations(e.target.value);
        const clearBtn = document.getElementById("clear-search");
        if (clearBtn) {
          clearBtn.style.display = e.target.value ? "block" : "none";
        }
      });
    }

    // Status filter (Semua / Online / Offline)
    document
      .querySelectorAll('input[name="filter-pos-status"]')
      .forEach((radio) => {
        radio.addEventListener("change", () => {
          this.filterStations();
        });
      });

    // AWS sensor change
    $("#aws-sensors").on("change", () => {
      StationModule.handleSensorChange();
    });
  },

  /**
   * Soft AJAX refresh: update leftbar last-reading + summary/marquee only.
   * Does not rebuild the page, reload map markers, or re-select stations/charts.
   */
  async refreshLeftbarData() {
    try {
      await this.loadSummary();
      if (
        typeof StationModule !== "undefined" &&
        typeof StationModule.refreshLeftbarSoft === "function"
      ) {
        await StationModule.refreshLeftbarSoft();
      }
      this.refreshMarqueeFromStations();
    } catch (error) {
      console.error("Leftbar soft refresh failed:", error);
    }
  },

  /**
   * Setup auto-refresh intervals
   */
  setupIntervals() {
    // Every 5 minutes: AJAX-update leftbar data only (no full-page feel / map reload)
    setInterval(async () => {
      await this.refreshLeftbarData();
    }, DashboardConfig.intervals.dataRefresh);
  },

  /**
   * Initialize UI components
   */
  initializeUIComponents() {
    // Initialize last active tab
    const lastTab = localStorage.getItem(
      DashboardConstants.STORAGE_KEYS.ACTIVE_TAB,
    );
    if (lastTab) {
      $(`#tab-center-left a[href="#${lastTab}"]`).tab("show");
    } else {
      $(`#tab-center-left a[href="#tab-awlr"]`).tab("show");
    }

    // Initialize Owl Carousel
    $(".owl-carousel").owlCarousel({
      items: 1,
      autoplay: false,
      loop: true,
      nav: true,
      dots: true,
    });

    // Initialize Magnific Popup
    $(".owl-carousel .item a").magnificPopup({
      type: "image",
      gallery: {
        enabled: true,
      },
    });

    // Register Chart.js plugins
    if (
      typeof Chart !== "undefined" &&
      typeof ChartDataLabels !== "undefined"
    ) {
      Chart.register(ChartDataLabels);
    }

    // Adjust padding
    setTimeout(DashboardHelpers.adjustPadding, 100);
  },

  /**
   * Filter stations by search term and online/offline status
   * @param {string} [searchTerm] - Search term (defaults to #filter-pos value)
   */
  filterStations(searchTerm) {
    const cards = document.querySelectorAll(
      ".card-station-awlr, .card-station-tmat, .card-station-arr, .card-station-awlr-arr, .card-station-aws, .card-station-fm, .card-station-wq, .card-station-avwr",
    );
    const searchInput = document.getElementById("filter-pos");
    const term = (
      typeof searchTerm === "string"
        ? searchTerm
        : (searchInput && searchInput.value) || ""
    )
      .toLowerCase()
      .trim();

    const statusRadio = document.querySelector(
      'input[name="filter-pos-status"]:checked',
    );
    const statusFilter = (statusRadio && statusRadio.value) || "all";

    cards.forEach((card) => {
      const name = (card.dataset.name || "").toLowerCase();
      const deviceId = (card.dataset.device || "").toLowerCase();
      const matchesSearch =
        !term || name.includes(term) || deviceId.includes(term);

      let matchesStatus = true;
      if (statusFilter === "online" || statusFilter === "offline") {
        const isOnline = DashboardHelpers.checkOnlineStatus(
          card.dataset.lastUpdate,
        );
        matchesStatus =
          statusFilter === "online" ? isOnline : !isOnline;
      }

      card.style.display = matchesSearch && matchesStatus ? "" : "none";
    });
  },

  /**
   * Load summary data
   */
  async loadSummary() {
    try {
      const response = await DashboardAPI.getSummaryStation();

      $("#total-station").text(response.total);
      $("#total-online").text(response.online);
      $("#total-offline").text(response.offline);
    } catch (error) {
      console.error("Error loading summary:", error);
    }
  },

  /**
   * @deprecated Marquee is built from StationModule state via refreshMarqueeFromStations()
   */
  async loadMarquee() {
    this.refreshMarqueeFromStations();
  },

  /** Pemisah marquee: titik tengah (lebih tenang dari ||) */
  _marqueeSep() {
    return '<span class="dc-marquee-sep" aria-hidden="true">·</span>';
  },

  _isStationOnline(station) {
    if (String(station.deviceStatus || "").toLowerCase() === "offline") {
      return false;
    }
    return DashboardHelpers.checkOnlineStatus(
      station.readingAt || station.readingHour,
    );
  },

  _marqueeOnlineIcon(station) {
    return this._isStationOnline(station)
      ? '<small class="mdi mdi-circle text-success"></small>'
      : '<small class="mdi mdi-circle text-danger"></small>';
  },

  /** Nama pos + label (Offline) jika offline (mis. siaga tapi alat offline). */
  _marqueeStationName(station) {
    const offlineLabel = !this._isStationOnline(station)
      ? ` <small class="text-muted">(Offline)</small>`
      : "";
    return `${station.name}${offlineLabel}`;
  },

  _marqueeDefault(message) {
    return `<span><small class="mdi mdi-circle text-success"></small> ${message}</span>`;
  },

  /** Status siaga: AWAS(1) / SIAGA(2) / WASPADA(3). 0 = normal. */
  _hasAlertStatus(station) {
    const status = Number(station.warningStatus);
    return !Number.isNaN(status) && status > 0;
  },

  /** Intensitas hujan > 0 (bukan "Tidak Ada Hujan"/berawan). */
  _hasRainIntensity(station) {
    const intensity = Number(
      station.intensityLastHour ?? station.intensity ?? 0,
    );
    return !Number.isNaN(intensity) && intensity > 0;
  },

  /** Badge siaga sama seperti di card (termasuk class blinking-content). */
  _marqueeWarningBadge(station) {
    return DashboardHelpers.getWarningStatusBadge(station.warningStatus).html;
  },

  /** Gambar intensitas hujan sama seperti di card. */
  _marqueeIntensityIcon(station) {
    if (station.intensityLastHour == null && station.intensity == null) {
      return "";
    }
    return DashboardHelpers.getIntensityIconMap(
      station.intensityLastHour ?? station.intensity,
    );
  },

  _formatWaterLevel(station) {
    if (station.waterLevel == null || station.waterLevel === "") return "-";
    const unit = station.unitDisplay ? ` ${station.unitDisplay}` : "";
    return `${DashboardHelpers.formatNumber(station.waterLevel)}${unit}`;
  },

  _formatRainfall(station) {
    const value =
      station.rainfallLastHour ?? station.rainfall ?? station.RainfallLastHour;
    if (value == null || value === "") return "-";
    return `${DashboardHelpers.formatNumber(value)} mm`;
  },

  _joinMarqueeItems(items) {
    return items
      .filter(Boolean)
      .map((html, index, arr) =>
        index === arr.length - 1 ? html : `${html}${this._marqueeSep()}`,
      )
      .join("");
  },

  /**
   * AWLR: default semua normal; jika ada siaga → tampilkan yang siaga (+ badge).
   */
  generateMarqueeAwlr(data) {
    if (!Array.isArray(data) || data.length === 0) return "";

    const alerts = data
      .filter((station) => this._hasAlertStatus(station))
      .sort((a, b) => Number(a.warningStatus) - Number(b.warningStatus));

    if (alerts.length === 0) {
      return this._marqueeDefault("Semua Pos Duga Air Normal");
    }

    return this._joinMarqueeItems(
      alerts.map((station) => {
        const icon = this._marqueeOnlineIcon(station);
        const name = this._marqueeStationName(station);
        const level = this._formatWaterLevel(station);
        const badge = this._marqueeWarningBadge(station);
        return `<span class="dc-marquee-item">${icon} ${name} ${badge} <small class="text-muted">(${level})</small></span>`;
      }),
    );
  },

  /**
   * ARR: default semua normal; jika ada intensitas hujan → tampilkan + gambar intensitas.
   */
  generateMarqueeArr(data) {
    if (!Array.isArray(data) || data.length === 0) return "";

    const raining = data
      .filter((station) => this._hasRainIntensity(station))
      .sort(
        (a, b) =>
          Number(b.intensityLastHour ?? 0) - Number(a.intensityLastHour ?? 0),
      );

    if (raining.length === 0) {
      return this._marqueeDefault(
        "Tidak Ada Hujan terjadi dalam 1 jam terakhir",
      );
    }

    return this._joinMarqueeItems(
      raining.map((station) => {
        const icon = this._marqueeOnlineIcon(station);
        const name = this._marqueeStationName(station);
        const rainfall = this._formatRainfall(station);
        const intensityCode = Number(
          station.intensityLastHour ?? station.intensity,
        );
        const intensityIcon = this._marqueeIntensityIcon(station);
        const intensityName = DashboardHelpers.getIntensityName(intensityCode);
        // Label intensitas di dalam kurung agar tidak terpotong native <marquee>
        return `<span class="dc-marquee-item">${icon} ${name} (${rainfall} · ${intensityName}) ${intensityIcon}</span>`;
      }),
    );
  },

  /**
   * AWLR+ARR: default semua normal; tampilkan yang siaga dan/atau ada intensitas hujan.
   */
  generateMarqueeAwlrArr(data) {
    if (!Array.isArray(data) || data.length === 0) return "";

    const notable = data.filter(
      (station) =>
        this._hasAlertStatus(station) || this._hasRainIntensity(station),
    );

    if (notable.length === 0) {
      return this._marqueeDefault("Semua Pos PDA & PCH Normal");
    }

    const sorted = [...notable].sort((a, b) => {
      // AWAS(1) → SIAGA(2) → WASPADA(3) → yang hanya hujan
      const aAlert = this._hasAlertStatus(a) ? Number(a.warningStatus) : 99;
      const bAlert = this._hasAlertStatus(b) ? Number(b.warningStatus) : 99;
      if (aAlert !== bAlert) return aAlert - bAlert;
      return (
        Number(b.intensityLastHour || 0) - Number(a.intensityLastHour || 0)
      );
    });

    return this._joinMarqueeItems(
      sorted.map((station) => {
        const icon = this._marqueeOnlineIcon(station);
        const name = this._marqueeStationName(station);
        const badge = this._hasAlertStatus(station)
          ? ` ${this._marqueeWarningBadge(station)}`
          : "";
        const mutedParts = [`TMA: ${this._formatWaterLevel(station)}`];
        let rainIcon = "";

        if (this._hasRainIntensity(station)) {
          mutedParts.push(`CH: ${this._formatRainfall(station)}`);
          rainIcon = ` ${this._marqueeIntensityIcon(station)}`;
        }

        return `<span class="dc-marquee-item">${icon} ${name}${badge} <small class="text-muted">(${mutedParts.join(" · ")})</small>${rainIcon}</span>`;
      }),
    );
  },

  /**
   * AWS (rainfall): default semua normal; jika ada intensitas/curah hujan → tampilkan.
   */
  generateMarqueeAws(data) {
    if (!Array.isArray(data) || data.length === 0) return "";

    const raining = data
      .filter((station) => {
        if (this._hasRainIntensity(station)) return true;
        const rainfall = Number(
          station.rainfallLastHour ?? station.rainfall ?? 0,
        );
        return !Number.isNaN(rainfall) && rainfall > 0;
      })
      .sort(
        (a, b) =>
          Number(b.intensityLastHour ?? 0) - Number(a.intensityLastHour ?? 0),
      );

    if (raining.length === 0) {
      return this._marqueeDefault("Semua Pos Klimatologi Normal");
    }

    return this._joinMarqueeItems(
      raining.map((station) => {
        const icon = this._marqueeOnlineIcon(station);
        const name = this._marqueeStationName(station);
        const rainfall = this._formatRainfall(station);
        const intensityIcon = this._hasRainIntensity(station)
          ? this._marqueeIntensityIcon(station)
          : "";
        return `<span class="dc-marquee-item">${icon} ${name} (CH: ${rainfall}) ${intensityIcon}</span>`;
      }),
    );
  },

  /**
   * Pick station with extreme numeric field (max/min). Ignores null/NaN.
   */
  _pickExtremeStation(stations, field, mode = "max") {
    let best = null;
    let bestValue = null;

    stations.forEach((station) => {
      const raw = station[field];
      if (raw == null || raw === "") return;
      const value = typeof raw === "number" ? raw : parseFloat(raw);
      if (Number.isNaN(value)) return;

      if (
        bestValue == null ||
        (mode === "max" ? value > bestValue : value < bestValue)
      ) {
        best = station;
        bestValue = value;
      }
    });

    return best ? { station: best, value: bestValue } : null;
  },

  /**
   * Marquee Flow Meter: volume bulan ini & totalizer terbanyak/tersedikit.
   */
  generateMarqueeFlowMeter(data) {
    if (!Array.isArray(data) || data.length === 0) return "";

    const sep = this._marqueeSep();
    const formatItem = (label, pick) => {
      if (!pick) return null;
      const { station, value } = pick;
      const isOnline = DashboardHelpers.checkOnlineStatus(
        station.readingHour || station.readingAt,
      );
      const icon = isOnline
        ? '<small class="mdi mdi-circle text-success"></small>'
        : '<small class="mdi mdi-circle text-danger"></small>';
      const formatted = DashboardHelpers.formatFlowMeterValue(value, station, {
        asHtml: false,
      });
      return `<span>${icon} ${label}: <strong>${station.name}</strong> (${formatted})</span>`;
    };

    const parts = [
      formatItem(
        "Volume Bulan Ini terbanyak",
        this._pickExtremeStation(data, "flowMonth", "max"),
      ),
      formatItem(
        "Volume Bulan Ini tersedikit",
        this._pickExtremeStation(data, "flowMonth", "min"),
      ),
      formatItem(
        "Totalizer terbanyak",
        this._pickExtremeStation(data, "flowTotal", "max"),
      ),
      formatItem(
        "Totalizer tersedikit",
        this._pickExtremeStation(data, "flowTotal", "min"),
      ),
    ].filter(Boolean);

    return parts.join(sep);
  },

  /**
   * Handle initialization error
   */
  handleInitError(error) {
    console.error("Dashboard initialization error:", error);

    const errorHtml = `
            <div class="alert alert-danger m-3" role="alert">
                <h4 class="alert-heading">Error Loading Dashboard</h4>
                <p>An error occurred while loading the dashboard. Please refresh the page.</p>
                <hr>
                <p class="mb-0">Error: ${error.message}</p>
            </div>
        `;

    $(".content").prepend(errorHtml);
  },
};
